import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { GraphicEq as IndicesIcon, InfoOutlined as InfoIcon } from '@mui/icons-material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { AppSchemasChartDeploymentComparison } from 'src/api/Api';
import ChartCard from './ChartCard';
import {
  ComparedDeployment,
  DeploymentOption,
  DeploymentPicker,
  SegmentedToggle,
  SeriesSwatch,
  ViewMode,
  ViewModeToggle,
} from './DeploymentControls';
import {
  INDEX_CONFIG,
  IndexConfig,
  UI,
  baseLineChartOptions,
  extent,
  normalizePoints,
  toNullableNumberArray,
} from './chartTheme';

type IndexKey = IndexConfig['key'];
type SingleIndexView = 'all' | IndexKey;
type MultiLayout = 'byIndex' | 'byDeployment';

const indexValues = (comparison: AppSchemasChartDeploymentComparison | undefined, key: IndexKey) =>
  toNullableNumberArray(comparison?.indices?.[key]);

const hasIndexData = (comparison?: AppSchemasChartDeploymentComparison) =>
  Object.values(comparison?.indices ?? {}).some((arr) => Array.isArray(arr) && arr.some((v) => typeof v === 'number'));

const mean = (values: (number | null)[]): number | null => {
  const finite = values.filter((v): v is number => v !== null);
  return finite.length ? finite.reduce((a, b) => a + b, 0) / finite.length : null;
};

const formatValue = (v: number | null) => (v === null ? '—' : Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(2));

export interface AcousticIndicesCardProps {
  mode: ViewMode;
  onModeChange: (mode: ViewMode) => void;
  multiDisabledReason?: string;
  primaryId?: string;
  primaryName: string;
  compared: ComparedDeployment[];
  deploymentsList: DeploymentOption[];
  onAddDeployment: (id: string) => void;
  onRemoveDeployment: (id: string) => void;
  comparisons: Map<string, AppSchemasChartDeploymentComparison>;
  timeSlots: string[];
  loading: boolean;
  error?: string | null;
  onRetry?: () => void;
  onUploadClick?: () => void;
}

const IndexLegend: React.FC<{ hidden: Set<IndexKey>; onToggle: (key: IndexKey) => void }> = ({ hidden, onToggle }) => (
  <Box role="group" aria-label="Indices shown" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
    {INDEX_CONFIG.map((idx) => {
      const off = hidden.has(idx.key);
      return (
        <ButtonBase
          key={idx.key}
          onClick={() => onToggle(idx.key)}
          aria-pressed={!off}
          title={idx.name}
          sx={{
            gap: 0.75,
            px: 1,
            py: 0.5,
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: 600,
            fontFamily: UI.font,
            color: off ? '#4A4A4A' : UI.textSecondary,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.04)', color: off ? UI.textMuted : UI.text },
          }}
        >
          <SeriesSwatch color={off ? '#333' : idx.color} />
          {idx.label}
        </ButtonBase>
      );
    })}
  </Box>
);

const AcousticIndicesCard: React.FC<AcousticIndicesCardProps> = ({
  mode,
  onModeChange,
  multiDisabledReason,
  primaryId,
  primaryName,
  compared,
  deploymentsList,
  onAddDeployment,
  onRemoveDeployment,
  comparisons,
  timeSlots,
  loading,
  error,
  onRetry,
  onUploadClick,
}) => {
  const isMulti = mode === 'multi';
  const [singleView, setSingleView] = useState<SingleIndexView>('all');
  const [multiLayout, setMultiLayout] = useState<MultiLayout>('byIndex');
  const [multiIndex, setMultiIndex] = useState<IndexKey>('aci');
  const [hiddenIndices, setHiddenIndices] = useState<Set<IndexKey>>(new Set());

  const primary = primaryId ? comparisons.get(primaryId) : undefined;
  const comparedWithData = useMemo(
    () => compared.filter((d) => hasIndexData(comparisons.get(d.id))),
    [compared, comparisons],
  );
  const missing = isMulti ? compared.filter((d) => !hasIndexData(comparisons.get(d.id))) : [];

  const toggleIndex = (key: IndexKey) => setHiddenIndices((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  // ── Single deployment ─────────────────────────────────────────────────────
  const singleOptions: Highcharts.Options = useMemo(() => {
    const normalized = singleView === 'all';
    const indices = normalized ? INDEX_CONFIG : INDEX_CONFIG.filter((c) => c.key === singleView);
    const series: Highcharts.SeriesOptionsType[] = indices.map((idx) => {
      const values = indexValues(primary, idx.key);
      return {
        type: 'line',
        name: `${idx.label} · ${idx.name}`,
        color: idx.color,
        data: normalized ? normalizePoints(values, extent([values])) : values,
      };
    });
    const selected = INDEX_CONFIG.find((c) => c.key === singleView);
    return {
      ...baseLineChartOptions({
        timeSlots,
        normalized,
        legend: normalized,
        yTitle: normalized ? 'Normalized value (0–1)' : `${selected?.label} value`,
      }),
      series,
    };
  }, [primary, singleView, timeSlots]);

  // ── Multi deployment · by index: deployments overlaid on one index ────────
  const byIndexOptions: Highcharts.Options = useMemo(() => {
    const label = INDEX_CONFIG.find((c) => c.key === multiIndex)?.label ?? multiIndex.toUpperCase();
    return {
      ...baseLineChartOptions({ timeSlots, yTitle: `${label} value` }),
      series: comparedWithData.map((d) => ({
        type: 'line',
        name: d.name,
        color: d.color,
        dashStyle: d.dash,
        data: indexValues(comparisons.get(d.id), multiIndex),
      })),
    };
  }, [comparedWithData, comparisons, multiIndex, timeSlots]);

  // ── Multi deployment · by deployment: small multiples on a shared scale ───
  const byDeploymentPanels = useMemo(() => {
    // Normalize each index against its range across ALL compared deployments,
    // so a panel's height is directly comparable with its neighbours'.
    const ranges = new Map(INDEX_CONFIG.map((idx) => [
      idx.key,
      extent(comparedWithData.map((d) => indexValues(comparisons.get(d.id), idx.key))),
    ]));
    return comparedWithData.map((d) => ({
      deployment: d,
      options: {
        ...baseLineChartOptions({ timeSlots, normalized: true, legend: false, height: 220, yTitle: '' }),
        series: INDEX_CONFIG.filter((idx) => !hiddenIndices.has(idx.key)).map((idx) => ({
          type: 'line',
          name: idx.label,
          color: idx.color,
          data: normalizePoints(indexValues(comparisons.get(d.id), idx.key), ranges.get(idx.key) ?? null),
        })),
      } as Highcharts.Options,
    }));
  }, [comparedWithData, comparisons, hiddenIndices, timeSlots]);

  const showsAllIndices = isMulti ? multiLayout === 'byDeployment' : singleView === 'all';
  const activeIndex = showsAllIndices
    ? undefined
    : INDEX_CONFIG.find((c) => c.key === (isMulti ? multiIndex : singleView));
  const description = isMulti && multiLayout === 'byDeployment'
    ? 'Each index is scaled to 0–1 using its range across every deployment shown, so panels can be compared directly. Click an index to hide or show it.'
    : activeIndex
      ? activeIndex.description
      : 'Each index is scaled to its own 0–1 range so their daily shapes can be compared. Hover a point for the raw value.';

  const indexToggle = (value: SingleIndexView, onChange: (v: SingleIndexView) => void, includeAll: boolean) => (
    <SegmentedToggle<SingleIndexView>
      ariaLabel="Acoustic index"
      size="small"
      value={value}
      onChange={onChange}
      options={[
        ...(includeAll ? [{ value: 'all' as const, label: 'All (normalized)' }] : []),
        ...INDEX_CONFIG.map((c) => ({ value: c.key, label: c.label })),
      ]}
    />
  );

  const controls = (
    <>
      {isMulti && (
        <DeploymentPicker
          compared={compared}
          deploymentsList={deploymentsList}
          onAdd={onAddDeployment}
          onRemove={onRemoveDeployment}
        />
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        {isMulti && (
          <SegmentedToggle<MultiLayout>
            ariaLabel="Comparison layout"
            size="small"
            value={multiLayout}
            onChange={setMultiLayout}
            options={[
              { value: 'byIndex', label: 'By Index' },
              { value: 'byDeployment', label: 'By Deployment' },
            ]}
          />
        )}
        {!isMulti && indexToggle(singleView, setSingleView, true)}
        {isMulti && multiLayout === 'byIndex' && indexToggle(multiIndex, (v) => setMultiIndex(v as IndexKey), false)}
        {isMulti && multiLayout === 'byDeployment' && <IndexLegend hidden={hiddenIndices} onToggle={toggleIndex} />}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
        <InfoIcon sx={{ fontSize: 14, color: UI.textMuted, mt: '1px' }} />
        <Typography sx={{ fontSize: '11.5px', color: UI.textSecondary, lineHeight: 1.5 }}>
          {activeIndex && <b style={{ color: UI.text }}>{activeIndex.name}. </b>}
          {description}
        </Typography>
      </Box>
    </>
  );

  const byIndexFooter = isMulti && multiLayout === 'byIndex' && activeIndex && (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 1 }}>
      {comparedWithData.map((d) => {
        const values = indexValues(comparisons.get(d.id), activeIndex.key);
        const finite = values.filter((v): v is number => v !== null);
        return (
          <Box key={d.id} sx={{ p: '10px 12px', borderRadius: '8px', border: `1px solid ${UI.hairline}`, bgcolor: 'rgba(255,255,255,0.015)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, minWidth: 0 }}>
              <SeriesSwatch color={d.color} dash={d.dash} />
              <Typography noWrap sx={{ fontSize: '12px', fontWeight: 700, color: UI.text }} title={d.name}>{d.name}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box>
                <Typography sx={{ fontSize: '18px', fontWeight: 800, color: UI.text, lineHeight: 1.1 }}>{formatValue(mean(values))}</Typography>
                <Typography sx={{ fontSize: '10px', color: UI.textMuted }}>daily mean</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '18px', fontWeight: 800, color: UI.text, lineHeight: 1.1 }}>
                  {formatValue(finite.length ? Math.max(...finite) : null)}
                </Typography>
                <Typography sx={{ fontSize: '10px', color: UI.textMuted }}>peak</Typography>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  const footer = (byIndexFooter || missing.length > 0) && (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {byIndexFooter}
      {missing.length > 0 && (
        <Typography sx={{ fontSize: '11px', color: UI.textMuted }}>
          No acoustic index data yet for {missing.map((m) => m.name).join(', ')}.
        </Typography>
      )}
    </Box>
  );

  const hasData = isMulti ? comparedWithData.length > 0 : hasIndexData(primary);

  return (
    <ChartCard
      eyebrow="Soundscape Complexity & Biodiversity"
      title="Acoustic Indices"
      subtitle={isMulti
        ? multiLayout === 'byIndex'
          ? <>{activeIndex?.label} compared across {compared.length} deployments</>
          : <>All indices, one panel per deployment</>
        : <>ACI, BI, ADI, AEI and NDSI over 24 hours at <b style={{ color: UI.text }}>{primaryName}</b></>}
      actions={<ViewModeToggle value={mode} onChange={onModeChange} multiDisabledReason={multiDisabledReason} />}
      controls={controls}
      loading={loading}
      error={error}
      onRetry={onRetry}
      hasData={hasData}
      empty={{
        icon: <IndicesIcon sx={{ fontSize: 36, color: '#444', mb: 0.5 }} />,
        title: 'No acoustic index data yet',
        body: isMulti
          ? 'Acoustic indices have not been computed for any of the selected deployments yet.'
          : 'Acoustic complexity, diversity and evenness indices have not been computed for this deployment yet.',
      }}
      onUploadClick={onUploadClick}
      footer={footer || undefined}
    >
      {!isMulti && <HighchartsReact highcharts={Highcharts} options={singleOptions} immutable />}
      {isMulti && multiLayout === 'byIndex' && <HighchartsReact highcharts={Highcharts} options={byIndexOptions} immutable />}
      {isMulti && multiLayout === 'byDeployment' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: comparedWithData.length > 1 ? '1fr 1fr' : '1fr' }, gap: 1.5 }}>
          {byDeploymentPanels.map(({ deployment, options }) => (
            <Box key={deployment.id} sx={{ border: `1px solid ${UI.hairline}`, borderRadius: '8px', p: '10px 12px 4px' }}>
              {/* No deployment swatch here: inside a panel, colour means index */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: '12px', fontWeight: 700, color: UI.text }} title={deployment.name}>
                  {deployment.name}
                </Typography>
                {deployment.isPrimary && <Typography sx={{ fontSize: '10px', color: UI.textMuted }}>primary</Typography>}
              </Box>
              <HighchartsReact highcharts={Highcharts} options={options} immutable />
            </Box>
          ))}
        </Box>
      )}
    </ChartCard>
  );
};

export default AcousticIndicesCard;
